import { useEffect, useId, useRef } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * The model's summarized thinking before it writes a letter. Streams in while the letter is
 * generated, then stays with the letter (here and in History) as a collapsible section.
 */
export function ThinkingPanel({
  text,
  open,
  onOpenChange,
  streaming = false,
}: {
  text: string
  open: boolean
  onOpenChange: (open: boolean) => void
  streaming?: boolean
}) {
  const bodyId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)

  // Follow the newest line while it streams.
  useEffect(() => {
    if (streaming && open && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [text, streaming, open])

  return (
    <div className="rounded-md border border-border bg-muted/40">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight size={13} className={cn('transition-transform motion-reduce:transition-none', open && 'rotate-90')} />
        Thinking
      </button>
      {open && (
        <div
          id={bodyId}
          ref={bodyRef}
          className="max-h-56 overflow-y-auto border-t border-border px-3 py-2.5 font-mono text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
        >
          {text || (streaming ? 'Thinking…' : '')}
          {streaming && text && <span className="animate-pulse motion-reduce:animate-none">▍</span>}
        </div>
      )}
    </div>
  )
}
